/**
 * ResourceProposalRow — compact + expanded row for new-resource proposals
 * (Phase 1.7).
 *
 * Visual treatment intentionally distinct from `SuggestionRow` (which
 * handles edit-suggestions on existing records). The differentiation
 * clarifies "proposed NEW resource" vs "proposed edit to X" at scan time
 * per opener §"Admin-review Pending tab UNION":
 *   - Left edge: warm-orange accent border (the existing SuggestionRow
 *     used the plain hairline) to indicate "new resource" creation intent.
 *   - Lead line: "Proposed new {target_type}: {payload.name|full_name}"
 *     instead of "Target: Field: old→new".
 *   - Status pip color map mirrors SuggestionRow for cross-row visual
 *     symmetry at the status discriminator.
 *
 * The row carries a renderExpanded prop the parent provides (typically
 * a ResourceProposalReviewPane); same shape as SuggestionRow so the
 * Pending tab UNION's render path stays uniform.
 */

import { useState, type ReactNode } from 'react';
import type { SuggestedResource } from '@/lib/types';

export interface ResourceProposalRowProps {
  proposal: SuggestedResource;
  /** Render the appropriate expanded review pane. */
  renderExpanded: () => ReactNode;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-500',
  approved: 'bg-green-500',
  rejected: 'bg-red-500',
  withdrawn: 'bg-sage-300',
};

const TARGET_LABELS: Record<string, string> = {
  organization: 'organization',
  individual: 'individual',
};

export function ResourceProposalRow({
  proposal,
  renderExpanded,
}: ResourceProposalRowProps): ReactNode {
  const [expanded, setExpanded] = useState(false);

  const proposedName = readProposedName(proposal);
  const statusColor = STATUS_COLORS[proposal.status] ?? 'bg-sage-300';
  const targetLabel = TARGET_LABELS[proposal.target_type] ?? proposal.target_type;

  return (
    <div className="border-l-4 border-warm-400 border-y border-r border-y-sage-800/[8%] border-r-sage-800/[8%] rounded-md mb-2 bg-white">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-label={`Proposed new ${targetLabel}: ${proposedName} (tap to ${expanded ? 'collapse' : 'expand'})`}
        className="w-full text-left px-3 py-2 min-h-11 flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-warm-600 mb-0.5">
            Proposed new {targetLabel}
          </div>
          <div className="text-sm font-semibold truncate">{proposedName}</div>
          <div className="text-xs text-ink-soft mt-0.5">
            <span
              aria-label={proposal.status}
              className={`inline-block w-2 h-2 rounded-full mr-1 ${statusColor}`}
            />
            {proposal.status} · {proposal.suggester_display} ·{' '}
            {formatRelativeTime(proposal.created_at)}
          </div>
        </div>
        <span aria-hidden="true" className="text-ink-soft text-lg">
          {expanded ? '▴' : '▾'}
        </span>
      </button>

      {expanded && renderExpanded()}
    </div>
  );
}

function readProposedName(p: SuggestedResource): string {
  // Phase 1.7 payload shape per validateOrganizationPayload /
  // validateIndividualPayload at api/_shared/repos/suggestedResources.ts.
  // Defensive — if the payload is missing the name field (shouldn't
  // happen post-validation), fall back to a generic label.
  const payload = p.payload as Record<string, unknown>;
  if (p.target_type === 'organization') {
    const name = payload['name'];
    if (typeof name === 'string' && name.length > 0) return name;
    return '(unnamed organization)';
  }
  const full = payload['full_name'];
  if (typeof full === 'string' && full.length > 0) return full;
  return '(unnamed individual)';
}

function formatRelativeTime(iso: string | null): string {
  if (iso === null) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
